import { useMemo } from "react";

import { type QueryBuilderState } from "~/components/canvas/easel/query-builder/QueryBuilderState";

import { isGroupable, isOrderable, isSelectable } from "../traverseValue/TypeTagInfo";
import {
	type ValuePath,
	type ValuePathSlice,
	joinPath,
	splitPathOnPrefix,
} from "../traverseValue/ValuePath";

/**
 * Indicates selection status of a field in the current active query, if one exists.
 *
 *   - Example 1: If the `orderby` menu is open in the querybuilder and the field's value is
 *     `isOrderable` and the field is used in the `orderby` clause, then `fieldSelectionState` will
 *     be `"selected"`
 *   - Example 2: If the `groupby` menu is open in the querybuilder and `isGroupable` does *NOT*
 *     return true for the field's value, then `fieldSelectionState` will be `"unselectable"`.
 */
export const useFieldActiveQuerySelectedState = (
	path: ValuePath,
	queryBuilderState: QueryBuilderState | null
): FieldSelectedState => {
	const pathFromActiveQueryRoot = usePathFromActiveQueryRoot(path, queryBuilderState);

	return useMemo(
		() => computeFieldSelectionState(pathFromActiveQueryRoot, queryBuilderState),
		[pathFromActiveQueryRoot, queryBuilderState]
	);
};

export type FieldSelectedState =
	// INTUITION: A field that is unselectable may or may not have an active query...
	| { fieldSelectionState: "unselectable"; pathFromActiveQueryRoot?: ValuePathSlice }
	// ... but a field that is selected or unselected *DEFINITELY* has an active query.
	| { fieldSelectionState: "selected" | "unselected"; pathFromActiveQueryRoot: ValuePathSlice };

const computeFieldSelectionState = (
	pathFromActiveQueryRoot: ValuePathSlice | undefined,
	queryBuilderState: QueryBuilderState | null
): FieldSelectedState => {
	if (!pathFromActiveQueryRoot) {
		return { pathFromActiveQueryRoot, fieldSelectionState: "unselectable" };
	}

	const path = joinPath(pathFromActiveQueryRoot.slice(1));
	const field = queryBuilderState?.get("fields").get(path);
	if (!field) {
		return { pathFromActiveQueryRoot, fieldSelectionState: "unselectable" };
	}

	switch (queryBuilderState?.get("currQueryNodeEditing")) {
		case "select":
			if (isSelectable(field.typeInfo)) {
				const selectedFields = queryBuilderState.get("selectedFields");
				const fieldSelectionState =
					selectedFields.get(path) !== undefined ? "selected" : "unselected";
				return { pathFromActiveQueryRoot, fieldSelectionState };
			}
			break;

		case "orderby":
			if (isOrderable(field.typeInfo)) {
				const orderedFields = queryBuilderState.get("orderedFields").orderedFields;
				const fieldSelectionState =
					orderedFields.get(path) !== undefined ? "selected" : "unselected";
				return { pathFromActiveQueryRoot, fieldSelectionState };
			}
			break;

		case "groupby":
			if (isGroupable(field.typeInfo)) {
				const groupedFields = queryBuilderState.get("groupedFields").groupedFields;
				const fieldSelectionState =
					groupedFields.get(path) !== undefined ? "selected" : "unselected";
				return { pathFromActiveQueryRoot, fieldSelectionState };
			}
			break;

		case "where":
		case "from":
		case "join":
		case null:
		case undefined:
			break;
	}

	return { pathFromActiveQueryRoot, fieldSelectionState: "unselectable" };
};

const usePathFromActiveQueryRoot = (
	path: ValuePath,
	queryBuilderState: QueryBuilderState | null
) => {
	return useMemo((): ValuePathSlice | undefined => {
		if (!queryBuilderState) {
			return;
		}
		const split = splitPathOnPrefix(path, queryBuilderState.get("pathToQueryRoot"));
		if (!split) {
			return;
		}

		const [, suffix] = split;
		return suffix;
	}, [queryBuilderState, path]);
};
